import React, { useCallback, useMemo, useState } from "react";
import { useVariableValue } from "@devcycle/react-client-sdk";
import { Pagination } from "antd5";
import { isEqual } from "lodash";
import useDeepCompareEffect from "use-deep-compare-effect";

import {
  convertDocumentFiltersToSearchRequest,
  DEFAULT_DOCUMENT_FILTERS,
  DocumentColumns,
  DocumentFilters,
  getDocumentColumnCopy,
  getDocumentTypeLabel,
  isValidSortField,
} from "components/documents/utils";
import PaywallPopover from "components/paywall/PaywallPopover";
import OrganisationsContainer from "components/record_details/OrganisationsPopover";
import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { numberSort, stringSort } from "lib/columnSort";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { NewRelevanceScore } from "lib/core_components/NewRelevanceScore";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { Table } from "lib/core_components/Table/Table";
import TableBanner from "lib/core_components/TableBanner";
import { RELEVANCY_SEARCH_NEW_USERS } from "lib/featureFlags";
import {
  DocumentsDto,
  DocumentsSearchResponse,
  useDocuments,
  UseSearchDocuments,
} from "lib/hooks/api/documents/useDocuments";
import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import DocumentIcon from "lib/icons/DocumentIcon";
import PaywallStar from "lib/icons/PaywallStar";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventNames, useTracking } from "lib/tracking";
import { getSignalScoreTooltipText, SignalEntityType } from "lib/utils/signalUtils";
import { useDocumentViewer } from "../../lib/providers/DocumentViewer";
import { usePaywall } from "../../lib/providers/Paywall";
import { LeadEntityType } from "../../lib/types/graphQLEnums";
import { Flex } from "../../styles/utility-components";
import CreateOrViewLeadButton from "../leads/saving/CreateOrViewLeadButton";
import { SaveLeadDrawerProvider } from "../leads/saving/SaveLeadDrawerProvider";
import { ViewLeadsDrawerProvider } from "../leads/viewing/ViewLeadsDrawerProvider";
import { useDocumentsTracking } from "./tracking";
import { useSavedViews } from "./useDocumentsPageState";

import css from "./DocumentTable.module.scss";

import DocumentSvg from "../../../assets/images/document.svg";

type SortState = Exclude<DocumentFilters["sort"], undefined>;

type DocumentResult = DocumentsSearchResponse["results"][0];
const EMPTY_DOCUMENT_RESULTS: DocumentsDto[] = [];

type Props = {
  filters: DocumentFilters;
  columnSettings: DocumentColumns[];
  selectedRows?: string[];
  onSortChange: (sort: SortState) => void;
  onSelectedRowsChange?: (selectedRowKeys: string[], selectedRows: DocumentsDto[]) => void;
  emptyText: JSX.Element;
  showBanner?: boolean;
  disablePreview?: boolean;
  documentProvider?: UseSearchDocuments;
  defaultFilters?: DocumentFilters;
};

function getSortOrder(key: string, sort?: SortState): "ascend" | "descend" | null {
  return sort?.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

function getDocumentColumns({
  filters,
  columnSettings,
  onDocumentClick,
  hasDocumentsDataType,
  disablePreview,
  isRelevancySearchEnabled,
  currentUser,
}: {
  filters: DocumentFilters;
  columnSettings: DocumentColumns[];
  onDocumentClick: (document: DocumentResult) => void;
  hasDocumentsDataType: boolean;
  disablePreview?: boolean;
  isRelevancySearchEnabled: boolean;
  currentUser: CurrentUserData | undefined;
}) {
  const sort = filters.sort;

  const title: ColumnType<DocumentsDto> = {
    title: getDocumentColumnCopy("title"),
    dataIndex: "title",
    key: "title",
    render: (_, d) => (
      <PaywallPopover featureType="DOCUMENTS">
        <div className={css.titleColumn}>
          <div className={css.titleContainer}>
            <DocumentIcon className={css.documentIcon} />
            <div className={css.title}>
              <EllipsisTooltipRouterLink
                fullText={d.title ?? "-"}
                linkProps={{
                  ...appRoutes.documentDetail(d.id),
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!disablePreview) {
                      onDocumentClick(d);
                    }
                  },
                  className: css.titleText,
                }}
                linkText={d.title}
              />
            </div>
          </div>
          {!hasDocumentsDataType && <PaywallStar className={css.paywallStar} />}
        </div>
      </PaywallPopover>
    ),
    sorter: stringSort((d) => d.title),
    sortOrder: getSortOrder("title", sort),
    sortDirections: ["ascend", "descend", "ascend"],
    showSorterTooltip: false,
    sizeConfig: {
      small: 200,
      medium: 300,
      large: 400,
      xlarge: 500,
    },
  };

  const buyers: ColumnType<DocumentsDto> = {
    ...commonTableColumns.titleColumn,
    title: getDocumentColumnCopy("buyers"),
    dataIndex: "buyers",
    key: "buyers",
    render: (_, d) => <OrganisationsContainer orgs={d.buyers ?? []} orgType="buyers" />,
    sizeConfig: {
      small: 200,
      medium: 300,
      large: 400,
      xlarge: 500,
    },
  };

  const signalScore: ColumnType<DocumentsDto> = {
    ...commonTableColumns.relevanceColumn,
    title: "Signal score",
    key: "signalScore",
    dataIndex: "signalScore",
    render: (_, d) => (
      <NewRelevanceScore
        relevanceScore={d.signalScore}
        id={d.id}
        entityType={SignalEntityType.Document}
      />
    ),
    sorter: numberSort((d: DocumentsDto) => d.signalScore),
    sortOrder: getSortOrder("signalScore", sort),
    sortDirections: ["ascend", "descend", "ascend"],
    showSorterTooltip: {
      title: getSignalScoreTooltipText("document"),
    },
  };

  const category: ColumnType<DocumentsDto> = {
    title: getDocumentColumnCopy("category"),
    dataIndex: "category",
    key: "category",
    render: (_, d) => (
      <span>{d.category ? getDocumentTypeLabel(d.category, currentUser) : "-"}</span>
    ),
    sorter: stringSort((d) => d.category),
    sortOrder: getSortOrder("category", sort),
    sortDirections: ["ascend", "descend", "ascend"],
    showSorterTooltip: false,
  };

  const publishedAt: ColumnType<DocumentsDto> = {
    title: getDocumentColumnCopy("publishedAt"),
    dataIndex: "publishedAt",
    key: "publishedAt",
    render: (_, d) => (d.publishedAt ? d.publishedAt.split("T")[0] : "-"),
    sorter: stringSort((d) => d.publishedAt),
    sortOrder: getSortOrder("publishedAt", sort),
    sortDirections: ["ascend", "descend", "ascend"],
    showSorterTooltip: false,
  };

  const actions: ColumnType<DocumentsDto> = {
    title: "Actions",
    key: "actions",
    render: (d: DocumentsDto) => (
      <Flex gap={8}>
        <CreateOrViewLeadButton
          trackingData={{
            "Context source": "In row",
          }}
          savedLeadIds={d.leads?.map((lead) => lead.id) || []}
          entityId={d.id}
          entityType="document"
          hasBuyerLeads={d.hasBuyerLeads ?? false}
          defaultValues={{
            name: d.title ?? "Untitled lead",
            assignedToId: currentUser?.guid,
            // The search DTO carries no AI summary; the backend appends the cached document
            // summary to this seeded link line on creation (see createLead in lead.resolver).
            description: `Lead created from [${d.title ?? "this document"}](/documents/${d.id})`,
            entities: [
              {
                entityId: d.id,
                entityType: LeadEntityType.Document,
              },
              ...(d.buyers ?? []).map((buyer) => ({
                entityId: buyer.id,
                entityType: LeadEntityType.Buyer,
              })),
            ],
          }}
        />
      </Flex>
    ),
  };

  const columns = {
    title,
    buyers,
    ...(!isRelevancySearchEnabled ? { signalScore } : {}),
    category,
    publishedAt,
    actions,
  };

  return columnSettings
    .map((c) => columns[c])
    .filter((c): c is ColumnType<DocumentsDto> => c !== undefined);
}

export function DocumentTable({
  filters,
  columnSettings,
  onSortChange,
  emptyText,
  showBanner,
  disablePreview,
  documentProvider = useDocuments,
  defaultFilters,
}: Props) {
  const { logEvent } = useTracking();
  const documentsTracking = useDocumentsTracking();
  const { data: currentUser } = useCurrentUser();

  const [pageSize, setPageSize] = useTablePageSize("document-table");
  const effectiveDefaultFilters = defaultFilters ?? DEFAULT_DOCUMENT_FILTERS;
  const { viewOrCustomFiltersApplied } = useSavedViews(effectiveDefaultFilters, false);
  const [currentPage, setCurrentPage] = useState(1);
  const { viewDocument } = useDocumentViewer();
  const { authorised: hasDocumentsDataType } = useCheckSubscription("DOCUMENTS");
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { openPaywallModal } = usePaywall();

  const { data, isLoading, isError } = documentProvider(
    convertDocumentFiltersToSearchRequest(filters, currentUser, { current: currentPage, pageSize }),
    {
      enabled: viewOrCustomFiltersApplied,
    },
  );

  const currentResultSet = data?.results || EMPTY_DOCUMENT_RESULTS;

  const onDocumentClick = useCallback(
    (document: DocumentResult) => {
      if (!hasDocumentsDataType) {
        openPaywallModal("DOCUMENTS");
        return;
      }
      // Register the current page as the prev/next sibling set and pass the table's
      // search filters through for keyword highlighting; the URL-state drawer handles
      // open/close and back-button restore.
      viewDocument(document.id, {
        siblingIds: currentResultSet.map((d) => d.id),
        filters,
      });
      documentsTracking.documentPreviewOpened(document.category ?? "-");
    },
    [
      documentsTracking,
      hasDocumentsDataType,
      openPaywallModal,
      viewDocument,
      currentResultSet,
      filters,
    ],
  );

  const columns = useMemo(() => {
    return getDocumentColumns({
      filters,
      columnSettings,
      onDocumentClick,
      hasDocumentsDataType,
      disablePreview,
      isRelevancySearchEnabled: isRelevancySearchEnabled,
      currentUser,
    });
  }, [
    filters,
    columnSettings,
    onDocumentClick,
    hasDocumentsDataType,
    disablePreview,
    isRelevancySearchEnabled,
    currentUser,
  ]);

  useDeepCompareEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <SaveLeadDrawerProvider trackingData={{ "Context source": "In row" }}>
      <ViewLeadsDrawerProvider>
        <div>
          <Table<DocumentsDto>
            ariaLabel="Document Table"
            dataSource={data?.results}
            loading={isLoading}
            columns={columns}
            showSorterTooltip={false}
            rowKey="id"
            onRow={(document) => ({
              onClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!disablePreview) {
                  onDocumentClick(document);
                }
              },
            })}
            onChange={(_, __, sorter) => {
              if (Array.isArray(sorter)) {
                sorter = sorter[0];
              }
              // If the sort order is not defined, we don't want to trigger a sort without an order
              if (!filters.sort && !sorter.order) {
                return;
              }

              const sortField = isValidSortField(sorter.columnKey?.toString());
              if (sortField) {
                const newSortState: SortState = {
                  order: sorter.order === "descend" ? "DESC" : "ASC",
                  field: sortField,
                };

                if (!isEqual(filters.sort, newSortState)) {
                  onSortChange?.(newSortState);
                }
              }
            }}
            isError={isError}
            locale={{
              emptyText,
            }}
          />
          {data?.totalResults !== undefined && data.totalResults > 0 && (
            <>
              {showBanner && (
                <TableBanner
                  title="Can't see the document you're looking for?"
                  description="We're still in the process of collecting some document types across certain buyers. You can check our progress in our tracker."
                  graphic={<img src={DocumentSvg} alt="Document" className={css.bannerImg} />}
                  ctaElement={
                    <Button
                      as="a"
                      href="https://stotles.notion.site/Documents-coverage-591e15112c894a92a78a19827df46237"
                      target="_blank"
                      icon="share03"
                    >
                      Open progress tracker
                    </Button>
                  }
                />
              )}
              <Pagination
                className={css.pagination}
                current={currentPage}
                defaultPageSize={pageSize}
                total={data.totalResults}
                onChange={(page: number, pageSize: number) => {
                  setPageSize(pageSize);
                  setCurrentPage(page);
                  logEvent(EventNames.paginationSelected, { Value: page });
                }}
              />
            </>
          )}
        </div>
      </ViewLeadsDrawerProvider>
    </SaveLeadDrawerProvider>
  );
}
